import { a as _inherits, b as _createSuper, c as _createClass, d as _classCallCheck, j as _get, k as _getPrototypeOf } from './_rollupPluginBabelHelpers-81f38430.js';
import { e } from './fluorescent-dom.es-0c4d37e6.js';

var ScrollSlider = customElements.get("scroll-slider-component");
if (!ScrollSlider) {
  console.error("ScrollSlider component not registered");
}
var DIRECTIONS = {
  FORWARDS: "forwards",
  BACKWARDS: "backwards"
};

// "Show adjacent slides" support.
// When enabled, the section renders a clone of the last slide before the
// first slide and a clone of the first slide after the last one, so the
// current slide always has a neighbour on both sides. Clones are not part
// of `sliderItems`; the index math is offset by the number of leading clones.
var ADJACENT_SELECTORS = {
  section: '[data-section-type="slideshow"]',
  widthProbe: "[data-slideshow-width-probe]",
  realSlide: ".scroll-slider__slider > *:not([data-slide-clone])",
  startClone: '.scroll-slider__slider > [data-slide-clone="start"]'
};
var ADJACENT_CLASSES = {
  currentSlide: "slideshow-slide--current"
};

// Mouse movement (px) during a drag that should cancel the follow-up click.
var CLICK_SUPPRESS_THRESHOLD = 5;
var Slideshow = /*#__PURE__*/function (_ScrollSlider) {
  _inherits(Slideshow, _ScrollSlider);
  var _super = _createSuper(Slideshow);
  function Slideshow() {
    _classCallCheck(this, Slideshow);
    return _super.apply(this, arguments);
  }
  _createClass(Slideshow, [{
    key: "connectedCallback",
    value: function connectedCallback() {
      var section = this.closest(ADJACENT_SELECTORS.section);
      this.section = section;
      this.widthProbe = section ? section.querySelector(ADJACENT_SELECTORS.widthProbe) : null;
      this.showAdjacentSlides = !!section && section.dataset.showAdjacentSlides === "true";
      this.slideOffset = 0;
      this.suppressNextClick = false;
      if (this.showAdjacentSlides) {
        // Only real slides count towards pages / dots / inert handling.
        this.selectors.slide = ADJACENT_SELECTORS.realSlide;
        this.slideOffset = this.querySelectorAll(ADJACENT_SELECTORS.startClone).length;
      }
      _get(_getPrototypeOf(Slideshow.prototype), "initSlider", this).call(this);
      this.autoplay = this.shouldAutoplay ? new this.PauseableInterval(this.autoAdvanceSlideshow.bind(this), this.autoplayDelay) : null;
      this.initSlideshowEvents();
    }

    // For some reason, adding this empty function prevents the slideshow from
    // breaking in the theme editor when the section gets moved.
    //
    // CustomeElement lifecycle method
  }, {
    key: "disconnectedCallback",
    value: function disconnectedCallback() {}
  }, {
    key: "initSlideshowEvents",
    value: function initSlideshowEvents() {
      this.events.push(e(document, "scroll-slider-".concat(this.id, ":slide-changed"), this.slideChanged.bind(this)));
      if (this.shouldAutoplay) {
        this.events.push(e(document, "scroll-slider-".concat(this.id, ":play"), this.play.bind(this)), e(document, "scroll-slider-".concat(this.id, ":pause"), this.pause.bind(this)));
      }
      if (this.shouldAutoplay && this.shouldPauseOnHover) {
        this.events.push(e(this.slider, "mouseenter", this.pause.bind(this)));
      }
      if (this.showAdjacentSlides) {
        var _this0 = this;
        // Clicking a peeking slide navigates to it.
        this.events.push(e(this.slider, "mousedown", function () {
          _this0.suppressNextClick = false;
        }), e(this.slider, "click", this.handleAdjacentSlideClick.bind(this)));
      }
    }

    // --- Adjacent slide helpers -------------------------------------------
  }, {
    key: "measureBodyWidth",
    value: function measureBodyWidth() {
      // The probe is a hidden copy of the theme's standard content container,
      // so its inner width is the "body width" other sections use.
      if (!this.widthProbe || !this.section) return;
      var style = getComputedStyle(this.widthProbe);
      var width = this.widthProbe.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      if (width > 0) {
        this.section.style.setProperty("--adjacent-slide-width-measured", "".concat(Math.round(width), "px"));
      }
    }
  }, {
    key: "getSlidePosition",
    value: function getSlidePosition(index) {
      return (index + this.slideOffset) * this.slideWidth;
    }
  }, {
    key: "goToSlide",
    value: function goToSlide(index) {
      var behavior = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : "instant";
      var total = this.sliderItems.length;
      if (!total || !this.slideWidth) return;
      // Wrap in both directions so the slideshow loops.
      var wrapped = (index % total + total) % total;
      this.scrollTo(this.getSlidePosition(wrapped), behavior);
    }
  }, {
    key: "handleAdjacentSlideClick",
    value: function handleAdjacentSlideClick(event) {
      if (this.suppressNextClick) {
        this.suppressNextClick = false;
        return;
      }
      var currentSlide = this.sliderItems[this.currentPage - 1];
      if (!currentSlide) return;

      // Non-current slides are inert, so the click target is the slider itself.
      // Work out which side of the current slide was clicked.
      var rect = currentSlide.getBoundingClientRect();
      if (event.clientX < rect.left) {
        event.preventDefault();
        this.goToSlide(this.currentPage - 2);
      } else if (event.clientX > rect.right) {
        event.preventDefault();
        this.goToSlide(this.currentPage);
      }
    }

    // --- ScrollSlider overrides needed for the clone offset ---------------
  }, {
    key: "initEvents",
    value: function initEvents() {
      var _this = this;
      if (!this.showAdjacentSlides) {
        _get(_getPrototypeOf(Slideshow.prototype), "initEvents", this).call(this);
        return;
      }
      this.events = [e(this.slider, "scroll", function () {
        return _this.handleScrollPositionChange();
      }), e(document, "scroll-slider-".concat(this.id, ":go-to-slide"), function (event) {
        _this.goToSlide(parseInt(event.detail.slideIndex, 10), event.detail.behavior);
      })];
      if (this.prevButton) {
        this.events.push(e(this.prevButton, "click", function (e) {
          return _this.onButtonClick(e);
        }));
      }
      if (this.nextButton) {
        this.events.push(e(this.nextButton, "click", function (e) {
          return _this.onButtonClick(e);
        }));
      }
    }
  }, {
    key: "initScroller",
    value: function initScroller() {
      // Remember where we were so a resize keeps the same slide centered.
      var keepIndex = this.currentPage ? this.currentPage - 1 : null;
      // Size the slides to the body width before the base class measures them.
      if (this.showAdjacentSlides) this.measureBodyWidth();
      _get(_getPrototypeOf(Slideshow.prototype), "initScroller", this).call(this);
      if (!this.showAdjacentSlides || !this.sliderItemsToShow || !this.sliderItemsToShow.length) return;

      // A slideshow is always one slide per page. The base class derives this
      // from slider width / slide width which no longer holds once the
      // neighbours are visible.
      this.slidesPerPage = 1;
      this.slideWidthWithGap = this.slideWidth;
      this.totalPages = this.sliderItemsToShow.length;
      if (this.pageDotsElement && (!this.dots || this.dots.length !== this.totalPages)) {
        this.initPageDots();
      }
      var index = keepIndex !== null ? keepIndex : this.currentPage - 1;
      this.scrollTo(this.getSlidePosition(index), "instant");
      this.handleScrollPositionChange();
    }
  }, {
    key: "handleScrollPositionChange",
    value: function handleScrollPositionChange() {
      var _this2 = this;
      if (!this.showAdjacentSlides) {
        _get(_getPrototypeOf(Slideshow.prototype), "handleScrollPositionChange", this).call(this);
        return;
      }
      if (!this.slideWidth) return;
      var previousPage = this.currentPage;
      var total = this.sliderItemsToShow ? this.sliderItemsToShow.length : this.sliderItems.length;
      var rawIndex = Math.round(this.slider.scrollLeft / this.slideWidth) - this.slideOffset;
      this.currentPage = Math.min(Math.max(rawIndex, 0), total - 1) + 1;

      // Update page dots
      if (this.pageDotsElement && this.dots && this.dots.length) {
        this.pageDotsElement.classList.remove(this.classes.hidden);
        this.dots.forEach(function (dot) {
          return dot.classList.remove(_this2.classes.dotVisible);
        });
        var dotIndex = Math.min(this.currentPage - 1, this.dots.length - 1);
        this.dots[dotIndex].classList.add(this.classes.dotVisible);
        if (total === 1) {
          this.pageDotsElement.classList.add(this.classes.hidden);
        }
      }
      if (this.currentPage != previousPage) {
        document.dispatchEvent(new CustomEvent("scroll-slider-".concat(this.id, ":slide-changed"), {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItems[this.currentPage - 1]
          }
        }));
      }

      // Native swipes (touch) can land on a clone at either end. Once the
      // scroll settles, jump to the real slide it represents.
      clearTimeout(this.scrollSettleTimer);
      this.scrollSettleTimer = setTimeout(function () {
        return _this2.normalizeClonePosition();
      }, 150);
    }
  }, {
    key: "normalizeClonePosition",
    value: function normalizeClonePosition() {
      if (!this.slideWidth) return;
      var total = this.sliderItems.length;
      var rawIndex = Math.round(this.slider.scrollLeft / this.slideWidth) - this.slideOffset;
      if (rawIndex < 0 || rawIndex >= total) {
        this.goToSlide(rawIndex, "instant"); // goToSlide wraps the index
      }
    }
  }, {
    key: "updateSlideVisibility",
    value: function updateSlideVisibility() {
      var _this3 = this;
      _get(_getPrototypeOf(Slideshow.prototype), "updateSlideVisibility", this).call(this);
      this.sliderItems.forEach(function (slide, index) {
        slide.classList.toggle(ADJACENT_CLASSES.currentSlide, index === _this3.currentPage - 1);
      });
    }

    // --- Existing slideshow behaviour -------------------------------------
  }, {
    key: "handleDragScrollMouseMove",
    value: function handleDragScrollMouseMove(event) {
      var _this4 = this;
      if (!this.mouseIsDown) return;
      var mouseCurrentPositionX = event.pageX - this.slider.offsetLeft;
      if (Math.abs(this.mouseStartPositionX - mouseCurrentPositionX) > CLICK_SUPPRESS_THRESHOLD) {
        this.suppressNextClick = true;
      }
      this.debounce(function () {
        var moveThreshold = 20;
        var movedAmount = _this4.mouseStartPositionX - mouseCurrentPositionX;
        if (Math.abs(movedAmount) > moveThreshold) {
          var direction = movedAmount < 0 ? DIRECTIONS.BACKWARDS : DIRECTIONS.FORWARDS;
          _this4.handleDragScrollSlideChange(direction);
        }
      }, 100);
    }
  }, {
    key: "debounce",
    value: function debounce(fn, delay) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
      this.debounceTimer = setTimeout(function () {
        fn();
      }, delay);
    }
  }, {
    key: "handleDragScrollSlideChange",
    value: function handleDragScrollSlideChange(direction) {
      var currentIndex = this.currentPage - 1; // Note: currentPage is not 0-based
      var nextIndex = direction == DIRECTIONS.FORWARDS ? currentIndex + 1 : currentIndex - 1;
      this.goToSlide(nextIndex, "instant"); // wraps at either end
    }
  }, {
    key: "play",
    value: function play() {
      var _this$autoplay;
      if (this.pageDotsElement) this.pageDotsElement.dataset.isPlaying = "true";
      (_this$autoplay = this.autoplay) === null || _this$autoplay === void 0 ? void 0 : _this$autoplay.resume();
      this.slider.setAttribute("aria-live", "off");
    }
  }, {
    key: "pause",
    value: function pause() {
      var _this$autoplay2;
      if (this.pageDotsElement) this.pageDotsElement.dataset.isPlaying = "false";
      (_this$autoplay2 = this.autoplay) === null || _this$autoplay2 === void 0 ? void 0 : _this$autoplay2.pause();
      this.slider.setAttribute("aria-live", "polite");
    }
  }, {
    key: "slideChanged",
    value: function slideChanged() {
      // If autoplaying we should setup visible classes for animations and
      // handle inert for inner elements that can be focused out of frame
      if (this.isSlideshow) {
        this.updateSlideVisibility();
        if (this.shouldAutoplay && this.autoplay) {
          if (this.autoplay.isPaused()) {
            // Pauseable timer is currently null, and the pause timer needs
            // to be reset.
            this.autoplay.pausedTimeLeft = this.autoplayDelay;
          } else {
            // Pauseable timer can be fully reset on slide change.
            this.autoplay.reset();
          }
        }
      }
    }
  }, {
    key: "autoAdvanceSlideshow",
    value: function autoAdvanceSlideshow() {
      // currentPage is 1-based, so it is also the 0-based index of the next slide.
      this.goToSlide(this.currentPage, "instant");
    }

    // https://stackoverflow.com/a/11196395
  }, {
    key: "PauseableInterval",
    value: function PauseableInterval(callback, delay) {
      this.callback = callback;
      this.delay = delay;
      this.pausedTimeLeft = 0;
      this.timerIsPaused = false;
      this.timerHasPaused = false;
      this.triggerSetAt = new Date().getTime();
      this.timer = setInterval(this.callback, this.delay);
      this.reset = function () {
        this.clearTimer();
        this.pausedTimeLeft = 0;
        this.timerIsPaused = false;
        this.timerHasPaused = false;
        this.triggerSetAt = new Date().getTime();
        this.timer = setInterval(this.callback, this.delay);
      };
      this.getTimeLeft = function () {
        var now = new Date();
        var timeLeft = this.timerHasPaused ? this.pausedTimeLeft : this.delay;

        // Once a timer has been paused the remaining amount of time left
        // needs to be calculated from the paused time left.
        return timeLeft - (now - this.triggerSetAt) % this.delay;
      };
      this.pause = function () {
        this.pausedTimeLeft = this.getTimeLeft();
        this.timerIsPaused = true;
        this.timerHasPaused = true;
        this.clearTimer();
      };
      this.resume = function () {
        var _this5 = this;
        // A new trigger is set based on unpaused. PauseTimeLeft is used
        // as the setTimeout timer.
        this.triggerSetAt = new Date().getTime();
        this.timer = setTimeout(function () {
          _this5.callback();
          _this5.reset();
        }, this.pausedTimeLeft);
      };
      this.clearTimer = function () {
        if (this.timer) {
          clearInterval(this.timer);
          this.timer = null;
        }
      };
      this.isPaused = function () {
        return this.timerIsPaused;
      };
    }
  }, {
    key: "onDotClick",
    value: function onDotClick(event) {
      if (_get(_getPrototypeOf(Slideshow.prototype), "userIsTabbing", this).call(this)) {
        var _this$autoplay3;
        (_this$autoplay3 = this.autoplay) === null || _this$autoplay3 === void 0 ? void 0 : _this$autoplay3.pause();
      }
      if (this.showAdjacentSlides) {
        event.preventDefault();
        this.goToSlide(parseInt(event.target.dataset.goTo, 10), "instant");
        return;
      }
      _get(_getPrototypeOf(Slideshow.prototype), "onDotClick", this).call(this, event);
    }
  }, {
    key: "onButtonClick",
    value: function onButtonClick(event) {
      if (_get(_getPrototypeOf(Slideshow.prototype), "userIsTabbing", this).call(this)) {
        var _this$autoplay4;
        (_this$autoplay4 = this.autoplay) === null || _this$autoplay4 === void 0 ? void 0 : _this$autoplay4.pause();
      }
      _get(_getPrototypeOf(Slideshow.prototype), "onButtonClick", this).call(this, event);
    }
  }]);
  return Slideshow;
}(ScrollSlider);
if (!customElements.get("slideshow-component")) {
  customElements.define("slideshow-component", Slideshow);
}
